import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import CategoryIcon from '../CategoryIcon/CategoryIcon';
import { createResourceLocatorString } from '../../util/routes';
import { useCategoryStripData } from './useCategoryStripData';
import css from './CategoryStrip.module.css';

const ArrowRightIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/**
 * Reusable category grid (Lovable CategoryStrip).
 * mode: 'services' | 'deals'
 */
const CategoryStrip = props => {
  const {
    intl,
    history,
    routeConfiguration,
    mode = 'services',
    maxItems = 8,
    showViewAll = true,
    embedded = false,
    className,
    facetSearchContext,
    activeCategoryId,
  } = props;

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  const { items, loading, modeConfig } = useCategoryStripData(mode, {
    maxItems,
    facetSearchContext,
    level: 'parent',
  });

  if (!modeConfig || loading || items.length === 0) {
    return null;
  }

  const labelFor = item =>
    intl.formatMessage({
      id: `${modeConfig.translationKeyPrefix}.${item.id}`,
      defaultMessage: item.label || item.id,
    });

  const goToCategory = parentCategoryId => {
    // Parent category → the search page filtered by that parent, matching the home page and the
    // filter dropdown (services use categoryLevel1; deals use listingsCategories).
    const categoryParam =
      mode === 'deals'
        ? { 'publicData.listingsCategories': parentCategoryId }
        : { 'publicData.categoryLevel1': parentCategoryId };
    history.push(
      createResourceLocatorString(
        'AlgoliaSearchPage',
        routeConfiguration,
        { listingType: mode },
        categoryParam
      )
    );
  };

  const goToAllCategories = () => {
    history.push(
      createResourceLocatorString(
        'CategoriesPage',
        routeConfiguration,
        {},
        { mode: modeConfig.categoriesMode }
      )
    );
  };

  return (
    <section
      className={classNames(
        css.stripSection,
        embedded && css.embeddedInSearch,
        arabicLanguage && css.arabicDirection,
        className
      )}
    >
      <div className={css.stripInner}>
        <div className={css.stripHeader}>
          <h2 className={css.stripTitle}>
            <FormattedMessage id={modeConfig.titleMessageId} />
          </h2>
          {showViewAll ? (
            <button type="button" className={css.viewAllLink} onClick={goToAllCategories}>
              <FormattedMessage id={modeConfig.viewAllMessageId} />
              <ArrowRightIcon className={css.viewAllIcon} />
            </button>
          ) : null}
        </div>

        <ul className={css.stripGrid}>
          {items.map(item => (
            <li key={item.id}>
              <div
                className={classNames(
                  css.categoryCard,
                  activeCategoryId != null &&
                    String(item.id) === String(activeCategoryId) &&
                    css.categoryCardActive
                )}
                aria-current={
                  activeCategoryId != null && String(item.id) === String(activeCategoryId)
                    ? 'true'
                    : undefined
                }
                role="link"
                tabIndex={0}
                onClick={() => goToCategory(item.id)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    goToCategory(item.id);
                  }
                }}
              >
                <CategoryIcon category={item} className={css.categoryIcon} />
                <div className={css.categoryText}>
                  <div className={css.categoryLabel}>{labelFor(item)}</div>
                  <div className={css.categoryCount}>
                    <FormattedMessage
                      id={modeConfig.listingsCountMessageId}
                      values={{ count: item.count }}
                    />
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

CategoryStrip.propTypes = {
  intl: PropTypes.object.isRequired,
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  routeConfiguration: PropTypes.array.isRequired,
  mode: PropTypes.oneOf(['services', 'deals']),
  maxItems: PropTypes.number,
  showViewAll: PropTypes.bool,
  embedded: PropTypes.bool,
  className: PropTypes.string,
  facetSearchContext: PropTypes.shape({
    filterParams: PropTypes.arrayOf(PropTypes.string),
    aroundLatLng: PropTypes.string,
    aroundRadius: PropTypes.number,
  }),
  activeCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default injectIntl(CategoryStrip);
